import BodyText from 'LuxKit/Typography/BodyText'
import type { ComponentProps, ComponentType, KeyboardEventHandler, MouseEventHandler, ReactNode, Ref } from 'react'
import { useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import FormatCurrency from './FormatCurrency'
import Caption from 'LuxKit/Typography/Caption'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import type { SvgIconProps } from 'LuxKit/SvgIcon'
import NestableClickable from './NestableClickable'
import { formatSaleUnitLabel } from 'hooks/useSaleUnitFormatting'
import LoadingIndicator from './Loading/LoadingIndicator'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import IconButton from 'LuxKit/Button/IconButton'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import Heading from 'LuxKit/Typography/Heading'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import LineTagAltIcon from 'LuxKit/Icons/line/LineTagAltIcon'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Label from 'LuxKit/Label'
import LoyaltyTierPill from 'loyalty/components/LoyaltyTierPill'
import { useAppSelector } from 'hooks/reduxHooks'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import focusStyle from 'styles/utils/focusStyle'
import PriceRowSale from 'LuxKit/PricePoints/PriceRowSale'

const StyledIconButton = styled(IconButton)`
    transform: translateY(-${rem(4)}) translateX(${rem(4)});
`

const ContentGroup = styled(Group)`
  flex-grow: 1;
`

const Sheet = styled(Group)`
  padding: ${rem(20)};
  background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
`

const Loader = styled(LoadingIndicator)`
  padding: 0;
`

const AccessibleClickableDiv = styled.div`
  cursor: pointer;
  
  &:focus-visible {
    ${focusStyle}
  }
`

interface Props {
  onClick?: () => void
  title: string
  IconComponent: ComponentType<SvgIconProps>
  price?: number
  priceUnit?: string
  isPriceLoading?: boolean
  to?: string
  currencyFormat?: ComponentProps<typeof FormatCurrency>['format']
  luxPlusPrice?: number
  className?: string
  tileType: 'car_hire' | 'flight' | 'transfer' | 'insurance' | 'cfmr' | 'tour'
  onDismissClick?: () => void
  discountPercentage?: number
  hasLoyaltyBonusPoints?: boolean
  alertText?: string
  onAlertClick?: () => void
  ref?: Ref<HTMLDivElement>
}

function CompactOrderUpsellTile(props: Props) {
  const {
    title,
    IconComponent,
    to,
    onClick,
    price,
    priceUnit,
    currencyFormat = 'roundedDollar',
    isPriceLoading,
    luxPlusPrice,
    className,
    tileType,
    onDismissClick,
    discountPercentage,
    hasLoyaltyBonusPoints,
    alertText,
    onAlertClick: alertClickCallback,
    ref,
  } = props

  const intl = useIntl()
  const formattedPriceUnit = formatSaleUnitLabel(intl, priceUnit, { withLeadingSlash: true })
  const showLuxPlusPricing = !!luxPlusPrice && !!formattedPriceUnit && !isPriceLoading
  const showPricing = !!price && !!formattedPriceUnit && !showLuxPlusPricing && !isPriceLoading
  const isCarHireTile = tileType === 'car_hire'
  const isFlightTile = tileType === 'flight'
  const isInsuranceBookingProtection = tileType === 'cfmr'

  const handleDismissClick = useCallback<MouseEventHandler<HTMLButtonElement>>(event => {
    event.preventDefault()
    onDismissClick?.()
  }, [onDismissClick])

  const handleAlertClick = useCallback<MouseEventHandler<HTMLDivElement>>((e) => {
    if (!alertClickCallback) return
    e.preventDefault()
    e.stopPropagation()
    alertClickCallback()
  }, [alertClickCallback])

  const handleAlertKeyDown = useCallback<KeyboardEventHandler<HTMLDivElement>>((e) => {
    if (!alertClickCallback) return
    // Enter activates on keyDown (standard button behavior)
    if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      alertClickCallback()
    }
    // Prevent Space from scrolling the page
    if (e.key === ' ') {
      e.preventDefault()
    }
  }, [alertClickCallback])

  const handleAlertKeyUp = useCallback<KeyboardEventHandler<HTMLDivElement>>((e) => {
    if (!alertClickCallback) return
    // Space activates on keyUp (matches onClick behavior and allows cancellation)
    if (e.key === ' ') {
      e.preventDefault()
      e.stopPropagation()
      alertClickCallback()
    }
  }, [alertClickCallback])

  const tier = useAppSelector(getLoyaltyDisplayTier)

  return <NestableClickable ref={ref} to={to} preventSwipeClick onClick={onClick} className={className}>
    <Sheet direction="vertical" tabletDirection="horizontal-reverse" gap={4} fullHeight fullWidth>
      <Group direction="horizontal" horizontalAlign="space-between" tabletDirection="vertical-reverse" tabletHorizontalAlign="end" tabletVerticalAlign="space-between" gap={8}>
        <IconComponent colour="highlight-primary-light-contrast" size="L" />
        <StyledIconButton kind="tertiary" onClick={handleDismissClick} size="small">
          <LineTimesIcon />
        </StyledIconButton>
      </Group>
      <ContentGroup direction="vertical" verticalAlign="space-between" gap={32}>
        <Group direction="vertical" desktopDirection="horizontal" gap={2} desktopGap={8} verticalAlign="center">
          <Heading variant="heading6">
            {title}
          </Heading>
          {(discountPercentage || hasLoyaltyBonusPoints) && <LabelGroup>
            {discountPercentage && <PromotionLabel icon={<LineTagAltIcon />} variant="default">{intl.formatMessage(
              {
                id: 'JchGbH3NYHrltWx_lBoS',
                defaultMessage: '{discountPercentage}% off',
                description: 'Discount percentage label on compact order upsell tile',
              },
              { discountPercentage },
            )}
            </PromotionLabel>}
            {hasLoyaltyBonusPoints && <LoyaltyTierPill kind="primary" tier={tier}>{intl.formatMessage({
              id: '7oIERe4Tgmkw00K8CpWP',
              defaultMessage: 'Bonus points',
              description: 'Loyalty bonus points label on compact order upsell tile',
            })}
            </LoyaltyTierPill>}
          </LabelGroup>}
        </Group>
        <div>
          {isPriceLoading && <Loader inline ballSize={8} />}
          {isCarHireTile && !showPricing && !showLuxPlusPricing && <Group direction="vertical">
            <BodyTextBlock variant="small" startIcon={<LineCheckIcon colour="highlight-primary-light-contrast" size="S" />}>
              {intl.formatMessage({
                id: 'kfEOB-Qc4bvPINdA2Tnu',
                defaultMessage: 'Free cancellation available',
                description: 'Feature text for car hire upsell tile in the car hire booking section',
              })}
            </BodyTextBlock>
            <BodyTextBlock variant="small" startIcon={<LineCheckIcon colour="highlight-primary-light-contrast" size="S" />}>
              {intl.formatMessage({
                id: 'dIxtqpQ63FRs_2Ytdnmq',
                defaultMessage: '24/7 support',
                description: 'Feature text for 24/7 support on upsell tile in the support area',
              })}
            </BodyTextBlock>
          </Group>}
          {isFlightTile && !showPricing && !showLuxPlusPricing && <Group direction="vertical">
            <BodyTextBlock variant="small" startIcon={<LineCheckIcon colour="highlight-primary-light-contrast" size="S" />}>
              {intl.formatMessage({
                id: 'MwLNl2E6ncIdVHBZYG1I',
                defaultMessage: 'Great fares from major airlines',
                description: 'Feature text for flight upsell tile in the flight search or booking flow',
              })}
            </BodyTextBlock>
            <BodyTextBlock variant="small" startIcon={<LineCheckIcon colour="highlight-primary-light-contrast" size="S" />}>
              {intl.formatMessage({
                id: 'dIxtqpQ63FRs_2Ytdnmq',
                defaultMessage: '24/7 support',
                description: 'Feature text for 24/7 support on upsell tile in the support area',
              })}
            </BodyTextBlock>
          </Group>}
          {!isPriceLoading && alertText && (
            <AccessibleClickableDiv
              role="button"
              tabIndex={0}
              onClick={handleAlertClick}
              onKeyDown={handleAlertKeyDown}
              onKeyUp={handleAlertKeyUp}
              aria-label={intl.formatMessage({
                id: '4zF2BxYG2qz5U8kg4oU8',
                defaultMessage: '{alertText}. Click for more information',
                description: 'Accessible label for alert button providing more details on compact order upsell tile',
              }, { alertText })}
            >
              <Label kind="plain" variant="urgency" endIcon={<LineInfoCircleIcon />}>
                {alertText}
              </Label>
            </AccessibleClickableDiv>
          )}
          {showPricing &&
            <div>
              {!isInsuranceBookingProtection && (
                <BodyText variant="medium">
                  {intl.formatMessage({
                    id: 'JPTyl9YYVkI_cn2R46F8',
                    defaultMessage: 'From',
                    description: 'Price prefix text on compact order upsell tile component',
                  })}
                </BodyText>
              )}
              <BodyText variant="medium">
                <FormattedMessage
                  id="DlBqiItiiAryaoPOUVgw"
                  defaultMessage="<bold>{price}</bold> {priceUnitLabel}"
                  description="Price with localised sale unit suffix on compact order upsell tile"
                  values={{
                    price: <FormatCurrency value={price} format={currencyFormat} />,
                    priceUnitLabel: formattedPriceUnit,
                    bold: (chunks: ReactNode) => <b>{chunks}</b>,
                  }}
                />
              </BodyText>
            </div>}
          {showLuxPlusPricing &&
            <div>
              <BodyText variant="medium">
                {intl.formatMessage({
                  id: 'JPTyl9YYVkI_cn2R46F8',
                  defaultMessage: 'From',
                  description: 'Price prefix text on compact order upsell tile component',
                })}
              </BodyText>
              <div>
                <PriceRowLuxPlusPrice
                  memberPrice={luxPlusPrice}
                  size="M"
                  saleUnit={priceUnit}
                  priceFormat="dynamicDollar"
                />
                <div>
                  <FormattedMessage
                    id="N4lCHxgP5AttBONm3Zfw"
                    defaultMessage="{price} <suffix>{priceUnitLabel}</suffix>"
                    description="Strikethrough price with localised sale unit suffix on compact order upsell tile LuxPlus pricing"
                    values={{
                      price: <PriceRowSale value={price ?? 0} priceFormat="dynamicDollar" />,
                      priceUnitLabel: formattedPriceUnit,
                      suffix: (chunks: ReactNode) => <Caption as="s" variant="large" colour="light-foreground-alt">{chunks}</Caption>,
                    }}
                  />
                </div>
              </div>
            </div>}
        </div>
      </ContentGroup>
    </Sheet>
  </NestableClickable>
}

export default CompactOrderUpsellTile
